import { throttle } from 'lodash';
import {
  onMounted,
  reactive,
  ref,
  toRef,
  computed,
  Ref,
  onBeforeUnmount,
  watch,
  getCurrentInstance,
  ComponentInternalInstance,
} from '@vue/composition-api';
import { RowEventContext, DialogPlugin, RowClassNameParams } from 'tdesign-vue';
import { PrimaryTableCol } from 'tdesign-vue/es/_common/js/table/types';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';

import i18n, { i18n_t } from '@/locales/i18n';
import uploadTaskManager from '@/renderer/scripts/utils/upload/index';
import { SubTask } from '@/renderer/scripts/utils/TaskManager/types';
import { FileInfo } from '@/renderer/scripts/utils/upload/types';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import smh, { cancelRequest, SMH } from '@/renderer/scripts/smh';
import extraFileInfoHandler from '@/renderer/components/SpacePage/compositions/extraFileInfoHandler';
import useImgPre from '@/renderer/components/SpacePage/compositions/useImagePreview';
import ipc from '@/renderer/scripts/utils/ipc';
import { DrawerEvents } from '@/renderer/store/modules/drawer';
import spaceATM from '@/renderer/scripts/utils/AccessTokenManager/space';
import { LOCATION_MAP } from '@/common/reporter/utils';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';
import { openJumpToWorkbenchDialog } from '@/renderer/scripts/composition/dialogHandler';

type TableData = {
  list: any[];
  NextMarker: number;
  crumbs: string[];
  selectedRowKeys: Array<Number | String>;
  selectedList: Array<SMH.SpaceFileItemData>;
  authorityList: SMH.AuthorityList;
};

export default function handler(spaceId: Ref<SpaceId>) {
  // 当前组件
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  const { $store: store } = vm;
  const { methods: extraFileInfoMethods } = extraFileInfoHandler(spaceId);
  const isMobileStyle = computed(() => store.getters.isMobileStyle);
  const disabledRowKeys = ref<Array<Number | String>>([]);
  const generalColumns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 21,
      disabled: ({ row, rowIndex }: { row: any; rowIndex: Number }) => disabledRowKeys.value.includes(rowIndex),
      // className: 'file-page__table-checkbox', // 设置type为multiple后设置className不生效
    },
    {
      colKey: 'name',
      title: 'title-name',
      titleDesc: i18n_t('项目名称'),
      sortType: 'all',
      sorter: true,
      width: 230,
      className: 'col-project-name',
    },
    {
      colKey: 'modificationTime',
      title: 'title-modificationTime',
      titleDesc: i18n_t('修改时间'),
      sortType: 'all',
      sorter: true,
      width: 150,
    },
    {
      colKey: 'size',
      title: 'title-size',
      titleDesc: i18n_t('大小'),
      sortType: 'all',
      sorter: true,
      width: 130,
      // 由于此栏承载了操作按钮组，因此宽度必须 > 130px
      fixed: 'right',
    },
  ];

  const mobileColumns: PrimaryTableCol[] = [
    {
      colKey: 'row-select',
      type: 'multiple',
      width: 21,
      disabled: ({ row, rowIndex }: { row: any; rowIndex: Number }) => disabledRowKeys.value.includes(rowIndex),
      // className: 'file-page__table-checkbox',
    },
    {
      colKey: 'mobile-name',
      title: 'mobile-name-header-slot',
      width: 180,
    },
    {
      colKey: 'mobile-operate',
      title: 'mobile-operate-header-slot',
      width: 80,
      align: 'right',
    },
  ];

  // 初始化columns
  const columns = computed(() => (isMobileStyle.value ? mobileColumns : generalColumns));
  const tableData = reactive<TableData>({
    list: [],
    NextMarker: 0,
    crumbs: [],
    selectedRowKeys: [],
    selectedList: [],
    authorityList: {
      canAuthorize: true,
      canDelete: true,
      canDownload: true,
      canModify: true,
      canPreview: true,
      canShare: true,
      canUpload: true,
      canView: true,
    },
  });

  // 初始化当前路由
  if (vm.$route.query?.path) {
    const { path } = vm.$route.query;
    tableData.crumbs = (path as string).split('/');
  }
  const pageData = reactive<PageData>({
    currPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 20 }),
        value: 20,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 50 }),
        value: 50,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 100 }),
        value: 100,
      },
      {
        label: i18n_t('每页 {slot1} 条', { slot1: 200 }),
        value: 200,
      },
    ],
  });
  const NextMarker = toRef(tableData, 'NextMarker');
  const crumbs = toRef(tableData, 'crumbs');
  const dirPath = ref(crumbs.value.join('/'));
  const loading = ref(false);
  const loadingOnHeader = ref(false);
  const isSyncFolder = ref(false);
  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('spacePage', {
    // 默认状态按时间倒序序排序
    sortBy: undefined,
    descending: true,
    sortType: undefined,
  });
  const useImagePreview = useImgPre();

  const methods = {
    async autoImagePreView(params: { e: WheelEvent }, rowHeight: number) {
      const spaceOrgId = +(vm.$route.query?.spaceOrgId || store.getters.orgId);
      const SpaceId =
        spaceId?.value || (vm.$route.query.spaceId as string) || (await spaceATM.getPersonalSpaceIdAsync());
      useImagePreview.debouncedFetchData(params, rowHeight, tableData, tableData.list, spaceOrgId, SpaceId);
    },
    async pullList(
      { silentLoading, needTips } = {
        silentLoading: false,
        needTips: false,
      },
    ): Promise<void> {
      const startTime = new Date().getTime();
      // 协作空间需要spaceOrgId
      const spaceOrgId = +(vm.$route.query?.spaceOrgId || store.getters.orgId);
      const SpaceId =
        spaceId?.value || (vm.$route.query.spaceId as string) || (await spaceATM.getPersonalSpaceIdAsync());
      sortMethods.changePage('spacePage', dirPath.value, SpaceId, spaceOrgId);
      const opt = {
        page: pageData.currPage,
        pageSize: pageData.pageSize,
        dirPath: dirPath.value,
        nextMarker: NextMarker.value,
        orderBy: innerSort.value.orderBy,
        orderByType: innerSort.value.orderByType,
        sortType: innerSort.value.orderBy ? innerSort.value.sortType : undefined,
        spaceId: SpaceId,
      };
      loading.value = !silentLoading;
      loadingOnHeader.value = silentLoading;
      try {
        const shareToken = (vm.$route.params?.shareToken as string) || (vm.$route.query?.shareToken as string);
        const { data: listRes } = await smh.commonAPI.listDirectory({
          ...opt,
          shareToken,
          spaceOrgId,
          cancelToken: cancelRequest.token,
        });
        const routeName = vm.$route.name;
        let list = listRes.contents.map((item: any, index: number) => ({
          ...item,
          id: index,
        }));
        if (routeName === 'share') {
          list = list.map((item: any) => ({
            ...item,
            historyId: item.versionId,
          })); // 在分页页，历史版本下下载，取historyId获取对应的版本，空间里不传，已保证一直取最新;
        } else {
          // 是否在一个不是自己企业的外部群组（如果是自己的企业就不算）, 如果是，则不显示敏感状态
          const inOuterGroup = spaceOrgId !== store.getters.orgId;
          const routerSpaceOrgId = isNaN(+spaceOrgId) ? undefined : spaceOrgId;
          list = list.map((item: any) => {
            return {
              ...item,
              ...(routerSpaceOrgId && {
                spaceOrgId: routerSpaceOrgId,
              }),
              sensitiveWordAuditStatus: inOuterGroup ? undefined : item.sensitiveWordAuditStatus,
            };
          });
          extraFileInfoMethods.getFileExtraRefresh(tableData, { targetList: list, noSensitiveWordTag: inOuterGroup });
        }
        const routeQuery = vm.$route.query?.path || '';
        if (routeQuery !== listRes.path.join('/') && vm.$route.name !== 'share') {
          // 若当前路由与请求返回的路径信息不一致，则丢弃掉本次数据，分享页面除外
          throw new Error('deprecatedData');
        }
        pageData.total = listRes.totalNum;
        isSyncFolder.value = !!listRes?.localSync;
        tableData.list = list;
        tableData.crumbs = listRes.path;
        tableData.authorityList = listRes.authorityList;
        useImagePreview.getImagePre(tableData, list, 0, 20, 20, spaceOrgId, SpaceId);
        if (loadingOnHeader.value && needTips) {
          tips.success(i18n_t('刷新成功'));
        }
        reporter.report('list_cost_time', {
          page: 'SpacePage',
          cost: new Date().getTime() - startTime,
          count: pageData.pageSize,
        });
      } catch (err: any) {
        // TODO: 临时判断，后续优化为路由守卫
        const { code, status } = err;
        if (err?.message === 'deprecatedData') {
          return;
        }
        if (['SpaceNotFound', 'NoSpace', 'NoPersonalSpace'].includes(err.code)) {
          if (err.code === 'NoPersonalSpace') {
            store.commit('SET_PERSONAL_SPACE', false);
          }
          openJumpToWorkbenchDialog(vm.$route.name as string);
        } else if (err.code === 'SpaceNotFound' && vm.$route.name === 'share') {
          tips.error(i18n_t('分享者空间已不存在，分享文件已失效'));
        } else {
          tips.error(err as MsgError);
        }
      } finally {
        loading.value = false;
        loadingOnHeader.value = false;
      }
      // 数据改变的时候，触发total改变
      ipc.emit(ipc.RENDERER_EVENTS_MAP.ON_DATA_LOADED, pageData.total);
    },
    changeCurrentPath(_path: string, action?: 'add' | 'replace' | 'remove', index?: number) {
      const { name, params, query } = vm.$route;
      if (_path === '') {
        crumbs.value = [];
      } else if (_path === '...') {
        crumbs.value.pop();
      } else if (index && [i18n_t('根目录'), query.name as string].includes(_path) && index < 1) {
        // 协作空间需要额外处理
        crumbs.value = [];
        if (name === 'collaborationSpaceList' && !query?.path) return; // 协作空间群组名下点击群组名，不动
      } else {
        switch (action) {
          case 'replace':
            // 此处不能直接赋值[_path],因为_path有可能是“同步10010/同步3”的形式
            if (_path?.includes('/')) {
              crumbs.value = _path.split('/');
            } else {
              crumbs.value = [_path];
            }
            break;
          case 'add':
            crumbs.value.push(_path);
            break;
          case 'remove':
            if (name === 'collaborationSpaceList' && index) {
              crumbs.value.splice((index - 1) as number); // 协作群组多加了个群组名
            } else {
              crumbs.value.splice(index as number);
              break;
            }
            break;
          default:
            break;
        }
      }

      // 有changCrumbs时才给dirPath赋值，避免分享页可能初始就有返回的path而出错
      dirPath.value = crumbs.value.join('/');
      const currRoutePath = vm.$route.query.path || '';
      if (currRoutePath !== dirPath.value) {
        if (name === 'relatedToMeFileList' && !dirPath.value) {
          dispatchRouter({
            name: 'relatedToMe',
          });
        } else if (name === 'favoriteList' && !dirPath.value) {
          // 返回我的收藏路由
          dispatchRouter({
            name: !store.getters.isMobileStyle ? 'workbench' : 'myFavorite',
            query: {
              tab: 'myFavorite',
            },
          });
        } else {
          // 共享页面不记住路径
          if (vm.$route.name !== 'share') {
            // currRoutePath子目录下点击协作空间，返回协作空间一级目录
            // FIX: 协作空间只有点击根目录才跳转至【协作空间】
            if (_path === i18n_t('协作空间') && name === 'collaborationSpaceList' && !dirPath.value) {
              dispatchRouter({ name: 'collaborationSpace' }, 'replace');
            } else {
              dispatchRouter(
                {
                  name: name || 'personalSpace',
                  params,
                  query: {
                    ...query,
                    path: dirPath.value,
                  },
                },
                'replace',
              );
            }
          }
          pageData.currPage = 1;
          this.refresh();
        }
      } else if (_path === i18n_t('协作空间') && name === 'collaborationSpaceList' && !dirPath.value) {
        // 协作空间需要额外处理
        dispatchRouter({ name: 'collaborationSpace' }, 'replace');
      } else if (name === 'share' && !dirPath.value) {
        // 分享外链的根目录需要额外处理
        pageData.currPage = 1;
        this.refresh();
      } else if (action === 'replace') {
        this.refresh();
      } else if (currRoutePath === dirPath.value && !loading.value && !loadingOnHeader.value) {
        this.silentRefresh();
      }
    },
    refresh(done?: () => any) {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      tableData.list = [];
      this.pullList().then(() => {
        done && done();
      });
    },
    silentRefresh(needTips = true) {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      this.pullList({
        silentLoading: true,
        needTips,
      });
      reporter.report('list_refresh', {
        location: LOCATION_MAP[vm.$route.name as string],
      });
    },
    resetData() {
      Object.assign(tableData, {
        list: [],
        NextMarker: 0,
        crumbs: [],
        selectedRowKeys: [],
        selectedList: [],
        authorityList: {
          canAuthorize: true,
          canDelete: true,
          canDownload: true,
          canModify: true,
          canPreview: true,
          canShare: true,
          canUpload: true,
          canView: true,
        },
      });
      Object.assign(pageData, {
        currPage: 1,
        pageSize: 20,
        total: 0,
        pageSizeOptions: [
          {
            label: i18n_t('每页 {slot1} 条', { slot1: 20 }),
            value: 20,
          },
          {
            label: i18n_t('每页 {slot1} 条', { slot1: 50 }),
            value: 50,
          },
          {
            label: i18n_t('每页 {slot1} 条', { slot1: 100 }),
            value: 100,
          },
          {
            label: i18n_t('每页 {slot1} 条', { slot1: 200 }),
            value: 200,
          },
        ],
      });
    },
    // 判断是否有selected
    checkSelected(rowIndex: number): boolean {
      return tableData.selectedRowKeys.indexOf(rowIndex) >= 0;
    },
    // 点选的控制 在这里无法实现，暂时隐去，改为纯CSS
    handleRowClassName(params: RowClassNameParams<any>): string {
      // const isSelected = methods.checkSelected(params?.rowIndex);
      // const selectedClassName = isSelected ? 'is-selected' : '';
      // const trClassName = '';
      // const res = [];
      // params.row.ext = { isSelected };
      return 'listRowClassName';
    },
    handleDbClickRow(context: RowEventContext<any>) {
      const { index, row: item, e } = context;
      const { target } = e;
      const { className } = target as HTMLElement;
      // 避免点击className部分过快，导致执行了一遍单击事件又执行了一遍双击事件
      if (item.type === 'dir' && typeof className === 'string' && !className.includes('file-name')) {
        methods.changeCurrentPath(item.name, 'add');
      }
    },
    onPageSizeChange(pageSize: number, pageInfo: any) {
      pageData.pageSize = pageSize;
      methods.refresh();
    },
    onCurrentChange(current: number, pageInfo: any) {
      pageData.currPage = current;
      methods.refresh();
    },
    /**
     * @description 监听上传刷新列表（频控）
     * @param {SubTask<FileInfo>} subTask
     */
    watchUploaderToUpdateCurrList: throttle(
      (subTask: SubTask<FileInfo>) => {
        const { info } = subTask;
        const { remoteFolder } = info;
        const isInSameDir = remoteFolder === dirPath.value;
        if (isInSameDir) {
          methods.silentRefresh(false);
        }
        // 刷新列表/上传文件夹 都刷新一下空间使用情况
        ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH_SAPCE_QUOTA);
      },
      1000,
      {
        leading: false,
        trailing: true,
      },
    ),
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    async sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.pullList();
      // 上报排序信息
      if (newSort) {
        reporter.report('sort', {
          sort_space: 'space_file_list',
          sortInfo: newSort,
        });
      }
    },
    updateDisabledRowKeys(rowKeys: Array<Number | String>) {
      disabledRowKeys.value = rowKeys;
    },
  };
  onMounted(() => {
    uploadTaskManager.on('subtask-finish', methods.watchUploaderToUpdateCurrList);
  });
  onBeforeUnmount(() => {
    uploadTaskManager.off('subtask-finish', methods.watchUploaderToUpdateCurrList);
  });

  // TODO loganylwu 后续设计自定义周期钩子处理
  const onWatchTotal = (val: any, oldVal?: any) => {
    // 如实记录data数据
    vm.$store.commit(DrawerEvents.SET_TOTAL, val);
  };
  // 分享外链不监听 spaceId 的变化，否则每次都会刷新 2 次
  vm.$route.name !== 'share' &&
    watch(spaceId, () => {
      if (vm.$route.query?.path) {
        const { path } = vm.$route.query;
        tableData.crumbs = (path as string).split('/');
      } else {
        tableData.crumbs = [];
      }
      methods.refresh();
    });
  watch(
    () => pageData.total,
    val => {
      onWatchTotal(val);
      if (!val) return;
      const newPageNum = Math.ceil(val / pageData.pageSize);
      if (pageData.currPage > newPageNum) {
        pageData.currPage = newPageNum;
        methods.refresh();
      }
    },
  );

  watch(
    () => vm.$route,
    (val, old) => {
      // 若是teamPath发生了改变, dirPath要从query取值初始化
      const newTeamPath = val.query.teamPath as string;
      if (newTeamPath !== old?.query.teamPath) {
        dirPath.value = (val.query?.path as string) || '';
      }
    },
    {
      immediate: true,
    },
  );

  return {
    columns,
    generalColumns,
    disabledRowKeys,
    loading,
    loadingOnHeader,
    currPath: dirPath,
    isSyncFolder,
    tableData,
    pageData,
    sort: tableSort,
    methods,
  };
}
